import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Drawer, { FormField, FormTextarea, FormSelect, getSaveLabel } from "@/components/Drawer";
import useBranches from "@/hooks/useBranches";

const EMPTY = {
  branch_id: "",
  date: new Date().toISOString().slice(0, 10),
  invoice_number: "",
  invoice_type: "jobcard",
  invoice_subtype: null,
  job_card_number: "",
  vehicle_registration_no: "",
  customer_name: "",
  oil: 0,
  spares: 0,
  consumables: 0,
  labour: 0,
  out_labour: 0,
  tax_amount: 0,
  extra_discount: 0,
  round_off: 0,
  total_invoice_value: 0,
  warranty_part_value: 0,
  amc_amount: 0,
  rsa_amount: 0,
  atw_amount: 0,
  technician: "",
  advisor: "",
  payment_lines: [],
  service_advance_id: "",
  advance_applied_amount: 0,
  add_on_1: "",
  add_on_2: "",
  add_on_3: "",
  notes: "",
};

const TYPE_OPTS = [
  { value: "jobcard", label: "Job Card" },
  { value: "counter_sale", label: "Counter Sale" },
  { value: "other", label: "Other" },
];
const SUBTYPE_OPTS = [
  { value: "amc", label: "AMC" },
  { value: "rsa", label: "RSA" },
  { value: "atw", label: "ATW" },
  { value: "insurance_renewal", label: "Insurance Renewal" },
];

/**
 * Create / edit a service invoice. When `editing` is null, POST /service/invoices
 * with `service.create`; otherwise PUT with `service.edit`.
 *
 * `branchId` (optional) — when supplied, the Advisor/Technician dropdowns
 * are filtered to staff tied to that branch. If left blank the drawer
 * loads all active staff of each role so single-branch installs still work.
 */
export default function ServiceInvoiceDrawer({ editing, branchId, defaultType, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [openAdvances, setOpenAdvances] = useState([]);
  const [advanceQuery, setAdvanceQuery] = useState("");
  const [selectedAdvance, setSelectedAdvance] = useState(null);
  const [advanceLoading, setAdvanceLoading] = useState(false);
  const [advisors, setAdvisors] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  // When the operator enters a valid JC #, we lock the fields we
  // copied from it — invoice must mirror the JC exactly. Cleared when
  // the JC # is emptied or the operator explicitly unlocks (edit mode
  // opens unlocked so admins can correct legacy typos).
  const [jcLocked, setJcLocked] = useState(false);
  const [jcLookupError, setJcLookupError] = useState("");
  // Fetch active SERVICE-type branches so the operator can pick the target
  // branch (backend rejects a service invoice against a sales branch).
  const { branches: serviceBranches } = useBranches({ activeOnly: true, kind: "service" });

  // Staff dropdowns filter by the *selected* branch (falling back to the
  // page-level filter if the drawer's own branch isn't picked yet).
  const staffBranchId = form.branch_id || branchId || "";
  useEffect(() => {
    (async () => {
      try {
        const params = { active_only: true };
        if (staffBranchId) params.branch_id = staffBranchId;
        const [ad, te] = await Promise.all([
          api.get("/service-staff", { params: { ...params, role: "advisor" } }),
          api.get("/service-staff", { params: { ...params, role: "technician" } }),
        ]);
        setAdvisors(ad.data || []);
        setTechnicians(te.data || []);
      } catch (err) {
        console.warn("Failed to load service staff", err);
      }
    })();
  }, [staffBranchId]);

  useEffect(() => {
    if (editing || !form.branch_id || !advanceQuery.trim()) {
      setOpenAdvances([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setAdvanceLoading(true);
      try {
        const params = { branch_id: form.branch_id, status: "open" };
        if (advanceQuery.trim()) params.search = advanceQuery.trim();
        const { data } = await api.get("/service-advances", { params });
        if (!cancelled) setOpenAdvances(data || []);
      } catch (err) {
        if (!cancelled) setOpenAdvances([]);
      } finally {
        if (!cancelled) setAdvanceLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [editing, form.branch_id, advanceQuery]);

  useEffect(() => {
    if (editing) return;
    setSelectedAdvance(null);
    setAdvanceQuery("");
    setForm((f) => (
      f.service_advance_id || f.advance_applied_amount
        ? { ...f, service_advance_id: "", advance_applied_amount: 0 }
        : f
    ));
  }, [editing, form.branch_id]);

  useEffect(() => {
    const customer = (form.customer_name || "").trim();
    if (!editing && !selectedAdvance && customer.length >= 2) {
      setAdvanceQuery(customer);
    }
  }, [editing, form.customer_name, selectedAdvance]);

  useEffect(() => {
    if (editing) {
      setForm({
        ...EMPTY,
        ...editing,
        date: (editing.date || "").slice(0, 10) || EMPTY.date,
        invoice_type: editing.invoice_type || "jobcard",
        invoice_subtype: editing.invoice_subtype || null,
        branch_id: editing.branch_id || "",
        service_advance_id: editing.service_advance_id || "",
        advance_applied_amount: Number(editing.advance_applied_amount || editing.advance_paid_amount || 0),
      });
      // Edit mode always opens unlocked — admins may need to correct
      // legacy invoices whose JC record no longer exists.
      setJcLocked(false);
      setSelectedAdvance(null);
      setAdvanceQuery("");
    } else {
      // Auto-select the page-level branch filter, or the single service
      // branch when only one exists — saves an extra click.
      const defaultBranch =
        branchId ||
        (serviceBranches.length === 1 ? serviceBranches[0].id : "");
      setForm({
        ...EMPTY,
        branch_id: defaultBranch,
        invoice_type: defaultType || "jobcard",
      });
      setJcLocked(false);
      setJcLookupError("");
      setSelectedAdvance(null);
      setAdvanceQuery("");
    }
  }, [editing, branchId, serviceBranches, defaultType]);

  // JC # → prefill lookup. Debounced 400ms so operators can type without
  // spamming the API. Only runs in *create* mode (edits shouldn't
  // overwrite a saved invoice's fields).
  useEffect(() => {
    if (editing) return;
    const jcNo = (form.job_card_number || "").trim();
    if (!jcNo) {
      setJcLocked(false);
      setJcLookupError("");
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const params = form.branch_id ? { branch_id: form.branch_id } : {};
        const { data: jc } = await api.get(
          `/job-cards/by-number/${encodeURIComponent(jcNo)}`,
          { params },
        );
        if (cancelled) return;
        setForm((f) => ({
          ...f,
          customer_name: jc.customer_name || "",
          vehicle_registration_no: jc.vehicle_reg_no || "",
          technician: jc.technician || "",
          advisor: jc.advisor || "",
          branch_id: f.branch_id || jc.branch_id || "",
          // Copy complaint into notes only if operator hasn't typed anything.
          notes: (f.notes || jc.complaint) || "",
        }));
        setJcLocked(true);
        setJcLookupError("");
      } catch (err) {
        if (cancelled) return;
        setJcLocked(false);
        setJcLookupError(err?.response?.status === 404 ? "Job Card # not found in this branch" : "");
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.job_card_number, form.branch_id, editing]);

  const num = (k) => (v) => setForm((f) => ({ ...f, [k]: v === "" ? 0 : Number(v) }));
  const str = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // Total is auto-derived from the buckets minus extra discount plus round-off
  // (Phase 2 revamp). Kept read-only in the UI to guarantee the invoice math
  // always reconciles with the printed breakdown.
  const autoTotal = useMemo(
    () =>
      ["oil", "spares", "consumables", "labour", "out_labour", "tax_amount"]
        .reduce((s, k) => s + (Number(form[k]) || 0), 0)
      - (Number(form.extra_discount) || 0)
      + (Number(form.round_off) || 0),
    [
      form.oil, form.spares, form.consumables, form.labour,
      form.out_labour, form.tax_amount, form.extra_discount, form.round_off,
    ],
  );

  const advanceAmount = Math.max(0, Number(form.advance_applied_amount) || 0);
  const cashDue = Math.max(0, autoTotal - advanceAmount);
  const setAdvance = (advanceId, amount) => {
    setForm((f) => {
      const total = Number(f.total_invoice_value) || 0;
      const lines = f.payment_lines || [];
      const paidSum = lines.reduce((s, pl) => s + (Number(pl.amount) || 0), 0);
      const previousCashDue = Math.max(0, total - (Number(f.advance_applied_amount) || 0));
      const wasMirrored = lines.length === 0
        || (lines.length === 1 && Math.abs(paidSum - previousCashDue) < 0.001);
      const nextCashDue = Math.max(0, total - amount);
      const nextLines = !wasMirrored
        ? lines
        : nextCashDue > 0
        ? [{ ...(lines[0] || { method: "cash", reference: "" }), amount: nextCashDue }]
        : [];
      return {
        ...f,
        service_advance_id: advanceId,
        advance_applied_amount: amount,
        payment_lines: nextLines,
      };
    });
  };

  // Sync the computed total into the form. `payment_lines` still uses
  // whatever the operator entered; the on-save validator makes sure the
  // sum matches. Only auto-mirror when the operator hasn't touched the
  // payments yet (single blank line worth 0 → seed with total).
  useEffect(() => {
    setForm((f) => {
      const nextAdvance = Math.max(0, Number(f.advance_applied_amount) || 0);
      const nextCashDue = Math.max(0, autoTotal - nextAdvance);
      if (Number(f.total_invoice_value) === autoTotal && Number(f.advance_applied_amount) === nextAdvance) {
        const currentPaid = (f.payment_lines || []).reduce(
          (s, pl) => s + (Number(pl.amount) || 0),
          0,
        );
        if (Math.abs(currentPaid - nextCashDue) < 0.001) return f;
      }
      const paidSum = (f.payment_lines || []).reduce(
        (s, pl) => s + (Number(pl.amount) || 0),
        0,
      );
      const previousCashDue = Math.max(
        0,
        (Number(f.total_invoice_value) || 0) - (Number(f.advance_applied_amount) || 0),
      );
      const wasMirrored = Math.abs(paidSum - previousCashDue) < 0.001;
      const lines = f.payment_lines || [];
      let nextLines = lines;
      if (wasMirrored) {
        if (lines.length === 0) {
          nextLines = nextCashDue > 0
            ? [{ method: "cash", amount: nextCashDue, reference: "" }]
            : [];
        } else if (lines.length === 1) {
          nextLines = nextCashDue > 0
            ? [{ ...lines[0], amount: nextCashDue }]
            : [];
        }
      }
      return {
        ...f,
        total_invoice_value: autoTotal,
        payment_lines: nextLines,
      };
    });
  }, [autoTotal]);

  // Computed sum of raw cost buckets — surfaced next to Total so the
  // operator sees the pre-discount subtotal at a glance.
  const partsLabourSum = useMemo(
    () => ["oil", "spares", "consumables", "labour", "out_labour", "tax_amount"]
      .reduce((s, k) => s + (Number(form[k]) || 0), 0),
    [form]
  );

  const onSave = async () => {
    if (!form.branch_id) {
      toast.error("Pick a service branch — invoices must be attributed to a branch.");
      return;
    }
    if (!form.invoice_number.trim()) {
      toast.error("Invoice number is required.");
      return;
    }
    if (form.invoice_type === "other" && !form.invoice_subtype) {
      toast.error("Pick a subtype for 'Other' invoices (AMC / RSA / ATW / Insurance Renewal).");
      return;
    }
    // Payment-line validation. Empty lines → invoice with ₹0 collected
    // (allowed for pending / DMS-only rows). Non-empty lines must sum to
    // the Total and every UPI line needs a UTR.
    const lines = (form.payment_lines || []).filter((pl) => Number(pl.amount) > 0);
    for (const pl of lines) {
      if (pl.method === "insurance" && !(pl.insurer || "").trim()) {
        toast.error("Enter the insurer name for the cashless insurance line.");
        return;
      }
      if (pl.method === "upi" && !(pl.reference || "").trim()) {
        toast.error("Every UPI payment line needs a UTR reference.");
        return;
      }
    }
    const paidSum = lines.reduce((s, pl) => s + Number(pl.amount || 0), 0);
    const total = Number(form.total_invoice_value) || 0;
    const advanceAmt = Number(form.advance_applied_amount) || 0;
    if (!editing && advanceAmt > 0 && !form.service_advance_id) {
      toast.error("Pick the service advance to adjust.");
      return;
    }
    if (!editing && form.service_advance_id && advanceAmt <= 0) {
      toast.error("Enter the advance amount to apply.");
      return;
    }
    if (selectedAdvance && advanceAmt > Number(selectedAdvance.remaining || 0) + 0.01) {
      toast.error(`Only ₹${Number(selectedAdvance.remaining || 0).toLocaleString("en-IN")} remains on this advance.`);
      return;
    }
    const coveredSum = paidSum + advanceAmt;
    if ((lines.length > 0 || advanceAmt > 0) && Math.abs(coveredSum - total) > 0.5) {
      toast.error(
        `Advance + payment must equal the invoice total. Covered ₹${coveredSum.toLocaleString("en-IN")}, `
        + `invoice ₹${total.toLocaleString("en-IN")}.`
      );
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing) {
        delete payload.service_advance_id;
        delete payload.advance_applied_amount;
      }
      // Non-'other' rows shouldn't carry a subtype (backend nulls it anyway).
      if (payload.invoice_type !== "other") payload.invoice_subtype = null;
      if (editing) {
        await api.put(`/service/invoices/${editing.id}`, payload);
      } else {
        await api.post("/service/invoices", payload);
      }
      toast.success(editing ? "Invoice updated" : "Invoice saved");
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <button
        type="button"
        onClick={onClose}
        data-testid="service-drawer-cancel"
        className="px-4 py-2 border border-canvas-line hover:border-ink text-sm font-mono uppercase"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        data-testid="service-drawer-save"
        className="px-4 py-2 bg-ink text-white text-sm font-mono uppercase hover:bg-ink/90 disabled:opacity-60"
      >
        {getSaveLabel(saving, editing, "Saving…", "Update", "Save")}
      </button>
    </>
  );

  return (
    <Drawer
      open
      onClose={onClose}
      eyebrow={editing ? "Edit Service Invoice" : "New Service Invoice"}
      title={editing ? `Invoice #${editing.invoice_number}` : "New Service Invoice"}
      footer={footer}
      testIdClose="service-drawer-close"
    >
      <div className="grid grid-cols-2 gap-4">
        <FormSelect
          label="Branch *"
          value={form.branch_id || ""}
          onChange={(v) => setForm((f) => ({ ...f, branch_id: v }))}
          options={[
            { value: "", label: "— select service branch —" },
            ...serviceBranches.map((b) => ({ value: b.id, label: b.name })),
          ]}
          disabled={!!editing}
          testId="svc-branch"
        />
        <FormField label="Date" type="date" value={form.date} onChange={str("date")} testId="svc-date" />
        <FormField label="Invoice # *" value={form.invoice_number} onChange={str("invoice_number")} testId="svc-inv-num" mono />
        {defaultType === "other" ? (
          // Add-ons flow: show subtypes DIRECTLY as the primary picker.
          // Under the hood we always store invoice_type=other; this dropdown
          // drives invoice_subtype only.
          <FormSelect
            label="Invoice Type *"
            value={form.invoice_subtype || ""}
            onChange={(v) => setForm((f) => ({
              ...f,
              invoice_type: "other",
              invoice_subtype: v || null,
              job_card_number: "",
            }))}
            options={[{ value: "", label: "— pick one —" }, ...SUBTYPE_OPTS]}
            testId="svc-inv-subtype"
          />
        ) : (
          <>
            <FormSelect
              label="Invoice Type *"
              value={form.invoice_type}
              onChange={(v) => setForm((f) => ({
                ...f,
                invoice_type: v,
                invoice_subtype: v === "other" ? f.invoice_subtype : null,
                job_card_number: v === "jobcard" ? f.job_card_number : "",
              }))}
              options={TYPE_OPTS.filter((o) => o.value !== "other")}
              testId="svc-inv-type"
            />
            <div />
          </>
        )}
        {form.invoice_type === "jobcard" && (
          <FormField
            label="Job Card #"
            value={form.job_card_number}
            onChange={str("job_card_number")}
            testId="svc-jc"
            mono
            hint={
              jcLookupError
                ? jcLookupError
                : jcLocked
                  ? "✓ Job Card details copied — fields locked"
                  : ""
            }
          />
        )}
        <FormField
          label="Vehicle Reg #"
          value={form.vehicle_registration_no}
          onChange={str("vehicle_registration_no")}
          testId="svc-veh"
          mono
          disabled={jcLocked}
        />
      </div>
      <FormField
        label="Customer Name"
        value={form.customer_name}
        onChange={str("customer_name")}
        testId="svc-cust"
        disabled={jcLocked}
      />

      <SectionHeader>Cost Breakdown</SectionHeader>
      {form.invoice_type === "other" ? (
        // AMC / Add-ons flow — only Invoice Amount + Tax + auto Total.
        // The `spares` field is reused as the storage bucket so the
        // existing autoTotal formula still works.
        <>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              label="Invoice Amount ₹"
              type="number"
              value={form.spares}
              onChange={num("spares")}
              testId="svc-invoice-amount"
            />
            <FormField
              label="Tax ₹"
              type="number"
              value={form.tax_amount}
              onChange={num("tax_amount")}
              testId="svc-tax"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Total ₹ (auto)
            </label>
            <div
              data-testid="svc-total"
              className="w-full px-3 py-2 bg-canvas-line/40 border border-canvas-line text-sm font-mono font-semibold"
            >
              ₹{Number(form.total_invoice_value || 0).toLocaleString("en-IN")}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4">
            <FormField label="Oil ₹" type="number" value={form.oil} onChange={num("oil")} testId="svc-oil" />
            <FormField label="Spares ₹" type="number" value={form.spares} onChange={num("spares")} testId="svc-spares" />
            <FormField label="Consumables ₹" type="number" value={form.consumables} onChange={num("consumables")} testId="svc-consumables" />
            <FormField label="Labour ₹" type="number" value={form.labour} onChange={num("labour")} testId="svc-labour" />
            <FormField label="Out-Labour ₹" type="number" value={form.out_labour} onChange={num("out_labour")} testId="svc-out-labour" />
            <FormField label="Tax ₹" type="number" value={form.tax_amount} onChange={num("tax_amount")} testId="svc-tax" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              label="Extra Discount ₹"
              type="number"
              value={form.extra_discount}
              onChange={num("extra_discount")}
              testId="svc-extra-discount"
            />
            <FormField
              label="Round-off ₹ (± manual)"
              type="number"
              value={form.round_off}
              onChange={num("round_off")}
              testId="svc-round-off"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                Total Invoice ₹ (auto)
              </label>
              <div
                data-testid="svc-total"
                className="w-full px-3 py-2 bg-canvas-line/40 border border-canvas-line text-sm font-mono font-semibold"
              >
                ₹{Number(form.total_invoice_value || 0).toLocaleString("en-IN")}
              </div>
            </div>
            <div className="text-xs text-ink-muted font-mono self-end pb-2">
              Subtotal: ₹{partsLabourSum.toLocaleString("en-IN")}
              {(Number(form.extra_discount) || 0) !== 0 && (
                <> · − ₹{Number(form.extra_discount).toLocaleString("en-IN")} discount</>
              )}
              {(Number(form.round_off) || 0) !== 0 && (
                <> · {Number(form.round_off) >= 0 ? "+" : ""}₹{Number(form.round_off).toLocaleString("en-IN")} round-off</>
              )}
            </div>
          </div>
        </>
      )}

      {form.invoice_type === "jobcard" && (
        <>
          <SectionHeader>Warranty</SectionHeader>
          <FormField
            label="Warranty Part Value ₹"
            type="number"
            value={form.warranty_part_value}
            onChange={num("warranty_part_value")}
            testId="svc-warranty"
          />
        </>
      )}
      {form.invoice_type === "other" && form.invoice_subtype && form.invoice_subtype !== "amc" && form.invoice_subtype !== "rsa" && form.invoice_subtype !== "atw" && form.invoice_subtype !== "insurance_renewal" && (
        <div />
      )}

      <SectionHeader>Personnel</SectionHeader>
      <div className="grid grid-cols-2 gap-4">
        <StaffPicker
          label="Technician"
          value={form.technician}
          onChange={str("technician")}
          options={technicians}
          testId="svc-tech"
          disabled={jcLocked}
        />
        <StaffPicker
          label="Advisor"
          value={form.advisor}
          onChange={str("advisor")}
          options={advisors}
          testId="svc-advisor"
          disabled={jcLocked}
        />
      </div>

      <SectionHeader>Payment</SectionHeader>
      {!editing && (
        <div className="border border-canvas-line bg-canvas-panel/40 p-3 space-y-3" data-testid="svc-advance-apply-panel">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
              Customer Advance Lookup
            </label>
            {selectedAdvance ? (
              <div className="border border-canvas-line bg-canvas p-3 flex items-start justify-between gap-3" data-testid="svc-advance-selected">
                <div>
                  <div className="font-mono text-xs font-semibold">{selectedAdvance.advance_no}</div>
                  <div className="text-sm font-medium">{selectedAdvance.customer_name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-muted mt-1">
                    ₹{Number(selectedAdvance.remaining || 0).toLocaleString("en-IN")} available
                    {selectedAdvance.customer_phone ? ` · ${selectedAdvance.customer_phone}` : ""}
                    {selectedAdvance.vehicle_reg_no ? ` · ${selectedAdvance.vehicle_reg_no}` : ""}
                    {selectedAdvance.jc_no ? ` · JC ${selectedAdvance.jc_no}` : ""}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedAdvance(null);
                    setAdvance("", 0);
                  }}
                  data-testid="svc-advance-clear"
                  className="px-2 py-1 border border-canvas-line font-mono text-[10px] uppercase hover:border-destructive hover:text-destructive"
                >
                  Clear
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={advanceQuery}
                  onChange={(e) => setAdvanceQuery(e.target.value)}
                  disabled={!form.branch_id}
                  placeholder="Type customer, phone, vehicle, JC # or advance #"
                  data-testid="svc-advance-lookup"
                  className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink disabled:opacity-60"
                />
                <div className="mt-2 space-y-1">
                  {advanceLoading && (
                    <div className="px-3 py-2 border border-dashed border-canvas-line font-mono text-[10px] text-ink-muted">
                      Searching advances…
                    </div>
                  )}
                  {!advanceLoading && openAdvances.slice(0, 6).map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => {
                        const amount = Math.min(Number(a.remaining) || 0, autoTotal);
                        setSelectedAdvance(a);
                        setAdvance(a.id, amount);
                      }}
                      data-testid={`svc-advance-option-${a.id}`}
                      className="w-full text-left px-3 py-2 bg-canvas border border-canvas-line hover:border-ink"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-mono text-xs font-semibold">{a.advance_no}</span>
                        <span className="font-mono text-xs">₹{Number(a.remaining || 0).toLocaleString("en-IN")} left</span>
                      </div>
                      <div className="text-sm mt-0.5">{a.customer_name}</div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-muted mt-0.5">
                        {[a.customer_phone, a.vehicle_reg_no, a.jc_no && `JC ${a.jc_no}`].filter(Boolean).join(" · ") || "No extra reference"}
                      </div>
                    </button>
                  ))}
                  {!advanceLoading && advanceQuery.trim() && openAdvances.length === 0 && (
                    <div className="px-3 py-2 border border-dashed border-canvas-line font-mono text-[10px] text-ink-muted" data-testid="svc-advance-no-results">
                      No open advance matches this customer/reference.
                    </div>
                  )}
                  {!advanceQuery.trim() && (
                    <div className="font-mono text-[10px] text-ink-subtle">
                      Customer name auto-fills this lookup after you type it above.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4 items-end">
            <FormField
              label="Advance Amount ₹"
              type="number"
              value={form.advance_applied_amount}
              onChange={(v) => setAdvance(
                form.service_advance_id,
                v === "" ? 0 : Number(v),
              )}
              disabled={!form.service_advance_id}
              testId="svc-advance-amount"
            />
            <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink-muted pb-2" data-testid="svc-advance-balance-copy">
              {selectedAdvance
                ? `Advance ₹${Number(advanceAmount || 0).toLocaleString("en-IN")} · Collect now ₹${cashDue.toLocaleString("en-IN")}`
                : "No advance selected"}
            </div>
          </div>
        </div>
      )}
      {editing && Number(editing.advance_paid_amount || 0) > 0 && (
        <div className="border border-canvas-line bg-canvas-panel/40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-ink-muted" data-testid="svc-linked-advance-note">
          Advance adjusted: ₹{Number(editing.advance_paid_amount || 0).toLocaleString("en-IN")}
        </div>
      )}
      <PaymentLinesEditor
        lines={form.payment_lines || []}
        total={cashDue}
        advanceAmount={advanceAmount}
        onChange={(next) => setForm((f) => ({ ...f, payment_lines: next }))}
      />

      <SectionHeader>Add-ons & Notes</SectionHeader>
      <div className="grid grid-cols-3 gap-4">
        <FormField label="Add-on 1" value={form.add_on_1} onChange={str("add_on_1")} testId="svc-addon-1" />
        <FormField label="Add-on 2" value={form.add_on_2} onChange={str("add_on_2")} testId="svc-addon-2" />
        <FormField label="Add-on 3" value={form.add_on_3} onChange={str("add_on_3")} testId="svc-addon-3" />
      </div>
      <FormTextarea label="Notes" value={form.notes} onChange={str("notes")} testId="svc-notes" />
    </Drawer>
  );
}

function SectionHeader({ children }) {
  return (
    <div className="pt-2 pb-1 border-b border-canvas-line font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
      {children}
    </div>
  );
}

/**
 * Advisor/Technician picker backed by the `service_staff` master.
 * Legacy invoices may carry a free-text name that's not in the master —
 * we surface those as a disabled "(legacy)" option so the value doesn't
 * silently disappear on edit. The "— none —" option clears the field.
 */
function StaffPicker({ label, value, onChange, options, testId, disabled }) {
  const knownNames = new Set((options || []).map((o) => o.name));
  const isLegacy = value && !knownNames.has(value);
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
        {label}
      </label>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        disabled={disabled}
        className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink disabled:opacity-70 disabled:bg-canvas-line/40 disabled:cursor-not-allowed"
      >
        <option value="">— none —</option>
        {isLegacy && <option value={value}>{value} (legacy)</option>}
        {(options || []).map((o) => (
          <option key={o.id} value={o.name}>
            {o.name}{o.code ? ` · ${o.code}` : ""}
          </option>
        ))}
      </select>
      {(options || []).length === 0 && (
        <div className="mt-1 font-mono text-[10px] text-ink-subtle">
          No entries yet — add via Service Staff master.
        </div>
      )}
    </div>
  );
}

function PaymentLinesEditor({ lines, total, advanceAmount = 0, onChange }) {
  const set = (idx, key, value) => {
    const next = lines.map((pl, i) => (i === idx ? { ...pl, [key]: value } : pl));
    onChange(next);
  };
  const add = () => {
    const paid = lines.reduce((s, pl) => s + (Number(pl.amount) || 0), 0);
    const remaining = Math.max(0, (Number(total) || 0) - paid);
    onChange([...lines, { method: "cash", amount: remaining, reference: "" }]);
  };
  const remove = (idx) => onChange(lines.filter((_, i) => i !== idx));
  const paidSum = lines.reduce((s, pl) => s + (Number(pl.amount) || 0), 0);
  const diff = (Number(total) || 0) - paidSum;
  return (
    <div className="space-y-2" data-testid="svc-payment-lines">
      {lines.length === 0 && (
        <div className="border border-dashed border-canvas-line px-3 py-4 text-center font-mono text-[11px] text-ink-subtle">
          {advanceAmount > 0
            ? "No new payment needed — this invoice is fully covered by the selected advance."
            : "No payment collected yet — add a tender to record cash / UPI."}
        </div>
      )}
      {lines.map((pl, idx) => (
        <div key={idx} className="grid grid-cols-1 sm:grid-cols-[110px_1fr_1fr_auto] gap-2 items-end" data-testid={`svc-payment-line-${idx}`}>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Method</label>
            <select
              value={pl.method}
              onChange={(e) => set(idx, "method", e.target.value)}
              data-testid={`svc-payment-line-${idx}-method`}
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            >
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="insurance">Insurance (cashless)</option>
            </select>
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Amount ₹</label>
            <input
              type="number"
              value={pl.amount}
              onChange={(e) => set(idx, "amount", e.target.value === "" ? 0 : Number(e.target.value))}
              data-testid={`svc-payment-line-${idx}-amount`}
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono focus:outline-none focus:border-ink"
            />
          </div>
          <div className={pl.method === "insurance" ? "grid grid-cols-2 gap-2" : ""}>
            {pl.method === "insurance" && (
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Insurer *</label>
                <input
                  type="text"
                  value={pl.insurer || ""}
                  onChange={(e) => set(idx, "insurer", e.target.value)}
                  placeholder="e.g. ICICI Lombard"
                  data-testid={`svc-payment-line-${idx}-insurer`}
                  className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
                />
              </div>
            )}
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                {pl.method === "upi" ? "UTR *" : pl.method === "insurance" ? "Claim no." : "Reference (opt.)"}
              </label>
              <input
                type="text"
                value={pl.reference || ""}
                onChange={(e) => set(idx, "reference", e.target.value)}
                data-testid={`svc-payment-line-${idx}-ref`}
                className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono focus:outline-none focus:border-ink"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => remove(idx)}
            data-testid={`svc-payment-line-${idx}-remove`}
            className="px-3 py-2 border border-canvas-line text-xs font-mono hover:border-destructive hover:text-destructive"
          >
            Remove
          </button>
        </div>
      ))}
      <div className="flex items-center justify-between text-[11px] font-mono">
        <button
          type="button"
          onClick={add}
          data-testid="svc-payment-line-add"
          className="px-3 py-1.5 border border-canvas-line hover:border-ink text-xs uppercase tracking-wider"
        >
          + Add tender
        </button>
        <div className="text-ink-muted">
          Collected: ₹{paidSum.toLocaleString("en-IN")}
          {" · "}
          Total: ₹{(Number(total) || 0).toLocaleString("en-IN")}
          {lines.length > 0 && Math.abs(diff) > 0.5 && (
            <span className={`ml-2 font-semibold ${diff > 0 ? "text-amber-600" : "text-destructive"}`}>
              {diff > 0 ? `↓ ₹${diff.toLocaleString("en-IN")} short` : `↑ ₹${(-diff).toLocaleString("en-IN")} over`}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

